"use client";

import type React from "react";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Empty, Divider, Tag, Pagination } from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  LockOutlined,
} from "@ant-design/icons";
import {
  userGetBookCategories,
  userGetBooks,
} from "../../apiservice/userService";
import {
  IBookCategoryData,
  IBookTypeData,
} from "../../apiservice/userService.type";
import {
  getCacheCategories,
  Cache_Category_Key,
} from "../../utils/apiSample/recommendation";

const AdvancedSearch = () => {
  const [formData, setFormData] = useState({
    searchTerm: "",
    category: "",
    isNewRelease: "",
  });
  const [searchResults, setSearchResults] = useState<IBookTypeData[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [categories, setCategories] = useState<IBookCategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const pageSize = 20;

  const fetchCategory = async () => {
    try {
      const cacheData = await getCacheCategories();
      setCategories(cacheData);
      const catgeoryData = await userGetBookCategories();
      if (catgeoryData) {
        setCategories(catgeoryData?.data);

        localStorage.setItem(
          Cache_Category_Key,
          JSON.stringify(catgeoryData?.data)
        );
      } else {
        setError("Category not found");
      }
    } catch (err) {
      setError("Failed to load book data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const { searchTerm, category, isNewRelease } = formData;

    setSearchResults([]);
    try {
      const bookData = await userGetBooks({
        searchString: searchTerm,
        categoryId: category,
        isNewRelease,
        page: 1,
        pageSize,
      });
      if (bookData) {
        setSearchResults(bookData?.data);
        setTotalPage(bookData?.meta?.total);
      } else {
        setError("Book not found");
      }
    } catch (err) {
      setError("Failed to load book data");
    } finally {
      setLoading(false);
    }

    setHasSearched(true);
    setCurrentPage(1);
  };

  const handleSearchPage = async () => {
    const { searchTerm, category, isNewRelease } = formData;

    setSearchResults([]);
    try {
      const bookData = await userGetBooks({
        searchString: searchTerm,
        categoryId: category,
        isNewRelease,
        page: currentPage,
        pageSize,
      });
      if (bookData) {
        setSearchResults(bookData?.data);
        setTotalPage(bookData?.meta?.total);
      } else {
        setError("Book not found");
      }
    } catch (err) {
      setError("Failed to load book data");
    } finally {
      setLoading(false);
    }

    setHasSearched(true);
  };

  useEffect(() => {
    if (hasSearched) {
      handleSearchPage();
    }
  }, [currentPage]);

  const handleReset = () => {
    setFormData({
      searchTerm: "",
      category: "",
      isNewRelease: "",
    });
    setSearchResults([]);
    setHasSearched(false);
  };

  // if (loading) {
  //   return <div className="p-4 text-center">Loading book details...</div>;
  // }

  // if (error || !searchResults) {
  //   return (
  //     <div className="p-4 text-center text-red-500">
  //       {error || "Book not found"}
  //     </div>
  //   );
  // }

  return (
    <div className="container mx-auto p-4">
      <Card
        title={
          <span className="text-gray-900 dark:text-stone-100">
            Advanced Search
          </span>
        }
        className="mb-6 bg-white dark:bg-stone-800 border-gray-200 dark:border-stone-700"
      >
        <form onSubmit={handleSearch}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label
                htmlFor="searchTerm"
                className="mb-2 text-sm font-medium text-gray-700 dark:text-stone-300"
              >
                Search Term
              </label>
              <div className="relative">
                <input
                  id="searchTerm"
                  name="searchTerm"
                  type="text"
                  value={formData.searchTerm}
                  onChange={handleInputChange}
                  placeholder="Title, author, or keywords"
                  className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-stone-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-stone-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-stone-100"
                />
                <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              </div>
            </div>

            <div className="flex flex-col">
              <label
                htmlFor="category"
                className="mb-2 text-sm font-medium text-gray-700 dark:text-stone-300"
              >
                Category
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-stone-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-stone-700 text-gray-900 dark:text-stone-100"
              >
                <option value="">Select a category</option>
                {(categories || []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label
                htmlFor="isNewRelease"
                className="mb-2 text-sm font-medium text-gray-700 dark:text-stone-300"
              >
                Release Status
              </label>
              <select
                id="isNewRelease"
                name="isNewRelease"
                value={formData.isNewRelease}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-stone-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-stone-700 text-gray-900 dark:text-stone-100"
              >
                <option value="">Select release status</option>
                <option value="true">New Release</option>
                <option value="false">Standard</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 dark:border-stone-600 rounded-md text-gray-700 dark:text-stone-300 hover:bg-gray-50 dark:hover:bg-stone-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            >
              Reset
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors inline-flex items-center gap-2"
            >
              <FilterOutlined />
              Search
            </button>
          </div>
        </form>
      </Card>

      {hasSearched && (
        <div>
          <Divider
            orientation="left"
            className="text-gray-900 dark:text-stone-100"
          >
            Search Results ({totalPage})
          </Divider>

          {searchResults.length > 0 ? (
            <div>
              <div className="grid grid-cols-1 gap-4">
                {(searchResults || []).map((book) => (
                  <Link
                    key={book.id}
                    to={`/users/book-details/${book.id}/${encodeURIComponent(
                      book.title.replace(/\s+/g, "_")
                    )}`}
                    className="no-underline text-inherit"
                  >
                    <div className="flex flex-row border border-gray-200 dark:border-stone-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200 bg-white dark:bg-stone-800">
                      <div className="flex-grow p-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-stone-100">
                          {book.title}
                        </h3>
                        <p className="text-gray-600 dark:text-stone-400">
                          {book.authorName}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-stone-500 mt-2">
                          {book.summary.substring(0, 150)}...
                        </p>

                        <div className="mt-2 flex flex-wrap gap-1">
                          {(book.categories || []).map((cat) => (
                            <Tag key={cat.id} color="blue">
                              {cat.bookCategory?.title}
                            </Tag>
                          ))}
                          {book.isNewRelease && (
                            <Tag color="orange">New Release</Tag>
                          )}
                          {!book.hasAccess && (
                            <LockOutlined
                              style={{ fontSize: "20px" }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="w-1/3 min-w-[120px]">
                        <img
                          alt={book.title}
                          src={`${process.env.PUBLIC_URL + book.coverImageUrl}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="mt-4 flex justify-center">
                <Pagination
                  current={currentPage}
                  onChange={setCurrentPage}
                  total={totalPage}
                  pageSize={pageSize}
                  hideOnSinglePage
                />
              </div>
            </div>
          ) : (
            <Empty
              description={
                <span className="text-gray-900 dark:text-stone-100">
                  No books found matching your criteria
                </span>
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;
